import { EngineInput, EngineResult, ExtractedEntities, GuardResult, PipelineTrace } from "../types";
import { extractEntities } from "../shared/entity_extractor";
import { checkGuard } from "../shared/guard_filter";
import { classify } from "../shared/classifier";
import * as conversationModel from "./conversation_model";
import * as incidentModel from "./incident_model";

// Krótki temat rozmowy (analogicznie do wzoru LLM online: "Imię temat")
function buildConversationTopic(entities: ExtractedEntities): string {
  const parts = [entities.arrangement, entities.topic, entities.failure].filter(Boolean) as string[];
  if (parts.length === 0) return "";
  const words = parts.join(" ").split(/\s+/).filter(Boolean).slice(0, 4);
  return words.join(" ");
}

function buildGuardResult(path: "A" | "B", transcript: string, guard: GuardResult, trace: PipelineTrace): EngineResult {
  return {
    path,
    transcript,
    entities: {},
    script: guard.warningText,
    shortNote: guard.warningText,
    title: "Odrzucono",
    guardStatus: guard.status,
    warningText: guard.warningText,
    reason: guard.reason,
    trace
  };
}

export async function process(input: EngineInput): Promise<EngineResult> {
  const baseTrace: PipelineTrace = {
    rawTranscript: input.transcript,
    firstGuardStatus: "OK",
    entities: {},
    finalGuardStatus: "OK",
    classificationPath: "A",
    classificationConfidence: 0,
    aScore: 0,
    bScore: 0,
    finalPath: "A",
    finalTitle: "",
    industry: input.industry,
    plantId: input.plantId,
    managerId: input.managerId,
    createdAt: Date.now(),
  };

  const firstGuard = checkGuard(input.transcript);
  baseTrace.firstGuardStatus = firstGuard.status;
  baseTrace.firstGuardReason = firstGuard.reason;
  if (firstGuard.status === "MOBBING_WARNING" || firstGuard.status === "PRIVACY_WARNING") {
    return buildGuardResult("A", input.transcript, firstGuard, { ...baseTrace, finalGuardStatus: firstGuard.status, finalGuardReason: firstGuard.reason });
  }

  const entities = await extractEntities(input.transcript, input.industry, input.plantId, input.managerId);
  baseTrace.entities = entities;
  const finalGuard = checkGuard(input.transcript, entities);
  baseTrace.finalGuardStatus = finalGuard.status;
  baseTrace.finalGuardReason = finalGuard.reason;
  if (finalGuard.status !== "OK") {
    return buildGuardResult("A", input.transcript, finalGuard, baseTrace);
  }

  const classification = classify(entities, input.transcript);
  baseTrace.classificationPath = classification.path;
  baseTrace.classificationConfidence = classification.confidence;
  baseTrace.aScore = classification.aScore;
  baseTrace.bScore = classification.bScore;
  const path = classification.path === "B" ? "B" : "A";

  const script = path === "B"
    ? incidentModel.render(entities)
    : conversationModel.render(entities);

  const title = path === "B"
    ? `Awaria: ${entities.equipment || "urządzenie"}`
    : (() => {
        const employee = entities.employee || "Pracownik X";
        const topic = buildConversationTopic(entities);
        return topic ? `${employee} ${topic}` : employee;
      })();

  const shortNote = script.replace(/\n/g, " ");

  baseTrace.finalPath = path;
  baseTrace.finalTitle = title;

  return {
    path,
    transcript: input.transcript,
    entities,
    script,
    shortNote,
    title,
    guardStatus: "OK",
    warningText: "",
    trace: baseTrace
  };
}
